import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Conta } from "@/types/database";

interface CreditCardEditFormProps {
  card: Conta | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreditCardEditForm: React.FC<CreditCardEditFormProps> = ({ 
  card, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    limite_credito: "",
    dia_vencimento: "",
    dia_fechamento: "",
    bandeira: "",
    cor: "#3B82F6",
    instituicao_nome: "",
  });

  // Populate form when card changes
  useEffect(() => {
    if (card) {
      setFormData({
        nome: card.nome || "",
        limite_credito: card.limite_credito?.toString() || "",
        dia_vencimento: card.dia_vencimento?.toString() || "",
        dia_fechamento: card.dia_fechamento?.toString() || "",
        bandeira: card.bandeira || "",
        cor: card.cor || "#3B82F6",
        instituicao_nome: card.instituicao_nome || "",
      });
    }
  }, [card]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!card) return;
    
    if (!formData.nome || !formData.limite_credito || !formData.dia_vencimento || !formData.dia_fechamento) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    const diaVencimento = parseInt(formData.dia_vencimento);
    const diaFechamento = parseInt(formData.dia_fechamento);
    
    if (diaVencimento < 1 || diaVencimento > 31) {
      toast.error("O dia de vencimento deve estar entre 1 e 31.");
      return;
    }
    
    if (diaFechamento < 1 || diaFechamento > 31) {
      toast.error("O dia de fechamento deve estar entre 1 e 31.");
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("contas")
        .update({
          nome: formData.nome,
          limite_credito: parseFloat(formData.limite_credito),
          dia_vencimento: diaVencimento,
          dia_fechamento: diaFechamento,
          bandeira: formData.bandeira || null,
          cor: formData.cor,
          instituicao_nome: formData.instituicao_nome || null,
        })
        .eq("id", card.id);

      if (error) {
        console.error("Erro ao atualizar cartão:", error);
        toast.error("Erro ao atualizar cartão de crédito.");
        return;
      }

      toast.success("Cartão de crédito atualizado com sucesso!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Erro inesperado:", error);
      toast.error("Erro inesperado ao atualizar cartão.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Cartão de Crédito</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome do Cartão *</Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => handleInputChange("nome", e.target.value)}
              placeholder="Ex: Nubank, Itaú, Bradesco..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="limite_credito">Limite de Crédito *</Label>
            <Input
              id="limite_credito"
              type="number"
              step="0.01"
              min="0"
              value={formData.limite_credito}
              onChange={(e) => handleInputChange("limite_credito", e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dia_vencimento">Dia Vencimento *</Label>
              <Input
                id="dia_vencimento"
                type="number"
                min="1"
                max="31"
                value={formData.dia_vencimento}
                onChange={(e) => handleInputChange("dia_vencimento", e.target.value)}
                placeholder="15"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dia_fechamento">Dia Fechamento *</Label>
              <Input
                id="dia_fechamento"
                type="number"
                min="1"
                max="31"
                value={formData.dia_fechamento}
                onChange={(e) => handleInputChange("dia_fechamento", e.target.value)}
                placeholder="10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bandeira">Bandeira</Label>
            <Select value={formData.bandeira} onValueChange={(value) => handleInputChange("bandeira", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a bandeira" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Visa">Visa</SelectItem>
                <SelectItem value="Mastercard">Mastercard</SelectItem>
                <SelectItem value="American Express">American Express</SelectItem>
                <SelectItem value="Elo">Elo</SelectItem>
                <SelectItem value="Hipercard">Hipercard</SelectItem>
                <SelectItem value="Diners Club">Diners Club</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="instituicao_nome">Instituição</Label>
            <Input
              id="instituicao_nome"
              value={formData.instituicao_nome}
              onChange={(e) => handleInputChange("instituicao_nome", e.target.value)}
              placeholder="Ex: Banco do Brasil, Itaú..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cor">Cor do Cartão</Label>
            <div className="flex items-center gap-2">
              <Input
                id="cor"
                type="color"
                value={formData.cor}
                onChange={(e) => handleInputChange("cor", e.target.value)}
                className="w-16 h-10 p-1 border rounded"
              />
              <Input
                value={formData.cor}
                onChange={(e) => handleInputChange("cor", e.target.value)}
                placeholder="#3B82F6"
                className="flex-1"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};